import type { ShopperAiTagId } from "@redotech/effect-api-shared/ids/definitions";
import {
  SupportTicketListItemResponse,
  type SupportTicketListItemResponse as SupportTicketListItemResponseType,
  type UpdateSupportTicketInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SupportTicketListQueryParams } from "@redotech/effect-api-shared/rpc/support-ticket";
import { Option } from "effect";

export const DEFAULT_SUPPORT_TICKET_PAGE_SIZE = 50;

/** Number of pages to prefetch ahead of the current page. */
export const SUPPORT_TICKET_PREFETCH_PAGES_AHEAD = 3;

export type SupportTicketListUrlParams = SupportTicketListQueryParams;

export type SupportTicketListFetchOptions = Pick<
  SupportTicketListUrlParams,
  "includeHighlights" | "includeTotalCount" | "searchAfter"
>;

function toOptionIfPresent<T>(
  value: T | null | undefined,
): Option.Option<T> | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === null ? Option.none() : Option.some(value);
}

export function buildSupportTicketListUrlParams(
  page: number,
  filterGroup: string | undefined,
  pageSize = DEFAULT_SUPPORT_TICKET_PAGE_SIZE,
  sorts?: string,
  options: SupportTicketListFetchOptions = {},
): SupportTicketListUrlParams {
  return {
    limit: pageSize,
    offset: page * pageSize,
    ...(options.searchAfter && options.searchAfter.length > 0
      ? { searchAfter: options.searchAfter }
      : {}),
    filterGroup,
    sorts,
    ...(options.includeTotalCount === undefined
      ? {}
      : { includeTotalCount: options.includeTotalCount }),
    ...(options.includeHighlights === undefined
      ? {}
      : { includeHighlights: options.includeHighlights }),
  };
}

/** Declared meaning of a tag write; the row holds only tags this client saw. */
export interface SupportTicketTagUpdateIntent {
  mode: "add" | "replace";
  tagIds: readonly ShopperAiTagId[];
}

export interface SupportTicketUpdateMutationMetadata {
  readonly [key: string]: unknown;
  tagUpdate: SupportTicketTagUpdateIntent;
}

export function supportTicketTagUpdateMetadata(
  mode: SupportTicketTagUpdateIntent["mode"],
  tagIds: readonly ShopperAiTagId[],
): SupportTicketUpdateMutationMetadata {
  return { tagUpdate: { mode, tagIds } };
}

function tagUpdateIntentFromMetadata(
  metadata: unknown,
): SupportTicketTagUpdateIntent | undefined {
  if (typeof metadata !== "object" || metadata === null) {
    return undefined;
  }
  const { tagUpdate } = metadata as { tagUpdate?: unknown };
  if (typeof tagUpdate !== "object" || tagUpdate === null) {
    return undefined;
  }
  const { mode, tagIds } = tagUpdate as Partial<SupportTicketTagUpdateIntent>;
  if ((mode !== "add" && mode !== "replace") || !Array.isArray(tagIds)) {
    return undefined;
  }
  return { mode, tagIds };
}

function toTagUpdate(
  changes: Partial<SupportTicketListItemResponseType>,
  metadata: unknown,
): UpdateSupportTicketInput["tagUpdate"] {
  const intent = tagUpdateIntentFromMetadata(metadata);
  if (intent) {
    return intent.mode === "add"
      ? { _tag: "add", tagIds: intent.tagIds }
      : { _tag: "replace", tagIds: intent.tagIds };
  }
  // An undeclared `tagIds` change states the row's full set.
  return changes.tagIds
    ? { _tag: "replace", tagIds: changes.tagIds }
    : undefined;
}

export function toUpdateSupportTicketInput(
  changes: Partial<SupportTicketListItemResponseType>,
  metadata?: unknown,
): UpdateSupportTicketInput {
  return {
    status: changes.status,
    priority: toOptionIfPresent(changes.priority),
    readAt: toOptionIfPresent(changes.readAt),
    mongoCustomerId: toOptionIfPresent(changes.mongoCustomerId),
    tagUpdate: toTagUpdate(changes, metadata),
    snoozedUntil: changes.snoozedUntil,
    // The list row exposes the subject as `emailSubject`; persist an edit to it
    // as a `display_subject` override on the ticket's email thread(s).
    displaySubject: changes.emailSubject ?? undefined,
  };
}

export function serializeSupportTicket(
  supportTicket: SupportTicketListItemResponseType,
): typeof SupportTicketListItemResponse.Encoded {
  const { smsOptedOutAt, ...rest } = supportTicket;
  return {
    ...rest,
    readAt: supportTicket.readAt?.toISOString() ?? null,
    lastCustomerMessageAt:
      supportTicket.lastCustomerMessageAt?.toISOString() ?? null,
    lastMerchantMessageAt:
      supportTicket.lastMerchantMessageAt?.toISOString() ?? null,
    createdAt: supportTicket.createdAt?.toISOString() ?? null,
    statusUpdatedAt: supportTicket.statusUpdatedAt?.toISOString() ?? null,
    snoozedUntil: supportTicket.snoozedUntil?.toISOString() ?? null,
    ...(smsOptedOutAt !== undefined
      ? { smsOptedOutAt: smsOptedOutAt?.toISOString() ?? null }
      : {}),
  };
}
